import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Loader2, Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_utility-finder-7/artifacts/wusjk2bl_1.png";

export const Login = () => {
  useEffect(() => {
    if (sessionStorage.getItem('sessionExpired')) {
      sessionStorage.removeItem('sessionExpired');
      toast.warning('A sua sessão expirou. Entre novamente para continuar.', { duration: 8000 });
    }
  }, []);
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [showLoginPwd, setShowLoginPwd] = useState(false);
  const [showRegPwd, setShowRegPwd] = useState(false);
  const [showRegConfirm, setShowRegConfirm] = useState(false);

  // Forgot password
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  // Force change password
  const [forceChange, setForceChange] = useState(false);
  const [forceToken, setForceToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);
  const [tempPassword, setTempPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await login(loginData.email, loginData.password);
      if (result?.user?.must_change_password) {
        setForceChange(true);
        setForceToken(result.access_token);
        setTempPassword(loginData.password);
        toast.info('Deve alterar a sua password antes de continuar');
      } else {
        toast.success('Bem-vindo de volta!');
        navigate(['super_admin', 'gestor'].includes(result?.user?.role) ? '/centro-comando' : '/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Credenciais inválidas');
    } finally {
      setLoading(false);
    }
  };

  const handleForceChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      toast.error('As passwords não coincidem');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('A password deve ter pelo menos 6 caracteres');
      return;
    }
    setChangingPwd(true);
    try {
      await axios.put(`${API_URL}/auth/change-password`, {
        current_password: tempPassword,
        new_password: newPassword,
      }, { headers: { Authorization: `Bearer ${forceToken}` } });
      toast.success('Password alterada com sucesso!');
      // Now login again with the new password
      await login(loginData.email, newPassword);
      setForceChange(false);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar password');
    } finally {
      setChangingPwd(false);
    }
  };

  const handleForgotPassword = () => {
    setForgotSent(true);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('As passwords não coincidem');
      return;
    }
    setLoading(true);
    try {
      await register(registerData.name, registerData.email, registerData.password);
      toast.success('Conta criada com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  // Force change password screen
  if (forceChange) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2D3E6F] to-[#4DD4E8] flex items-center justify-center p-4 overflow-y-auto">
        <div className="w-full max-w-md my-auto">
          <div className="text-center mb-6">
            <img src={LOGO_URL} alt="Adiraqui CRM" className="h-32 sm:h-40 mx-auto mb-3" />
          </div>
          <Card className="border-0 shadow-2xl rounded-2xl overflow-hidden">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-outfit">Alterar Password</CardTitle>
              <CardDescription>Por segurança, deve definir uma nova password antes de continuar.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForceChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nova password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type={showNewPwd ? 'text' : 'password'}
                      placeholder="Nova password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10 pr-10 h-11 rounded-lg"
                      required
                      minLength={6}
                      data-testid="force-new-password"
                    />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowNewPwd(!showNewPwd)}>
                      {showNewPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Confirmar nova password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="password"
                      placeholder="Confirmar password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className="pl-10 h-11 rounded-lg"
                      required
                      minLength={6}
                      data-testid="force-confirm-password"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full h-11 bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full font-semibold" disabled={changingPwd} data-testid="force-change-submit">
                  {changingPwd ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> A guardar...</> : 'Definir Nova Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2D3E6F] to-[#4DD4E8] flex items-center justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-md my-auto">
        {/* Logo */}
        <div className="text-center mb-6">
          <img src={LOGO_URL} alt="Adiraqui CRM" className="h-32 sm:h-40 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-white font-outfit">CRM</h1>
          <p className="text-white/80 text-sm mt-1">Gestão de Clientes e Vendas</p>
        </div>

        <Card className="border-0 shadow-2xl rounded-2xl overflow-hidden">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-outfit">Bem-vindo de volta</CardTitle>
                <CardDescription>Introduza os seus dados para aceder ao CRM</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="login-email" type="email" placeholder="seu@email.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        className="pl-10 h-11 rounded-lg" required data-testid="login-email"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="login-password"
                        type={showLoginPwd ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="pl-10 pr-10 h-11 rounded-lg" required data-testid="login-password"
                      />
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setShowLoginPwd(!showLoginPwd)} data-testid="toggle-login-password">
                        {showLoginPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button type="button" className="text-sm text-[#2D3E6F] hover:underline" onClick={() => { setForgotOpen(true); setForgotSent(false); setForgotEmail(''); }} data-testid="forgot-password-link">
                      Esqueceu a password?
                    </button>
                  </div>

                  <Button type="submit" className="w-full h-11 bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full font-semibold" disabled={loading} data-testid="login-submit">
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> A entrar...</> : 'Entrar'}
                  </Button>
                </form>
              </CardContent>
        </Card>

        <p className="text-center text-white/60 text-sm mt-6">
          © 2026 Adiraqui. Todos os direitos reservados.
        </p>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Recuperar Password</DialogTitle>
            <DialogDescription>
              {forgotSent
                ? 'O pedido foi enviado. Contacte o administrador do sistema para repor a sua password.'
                : 'Introduza o seu email para iniciar a recuperação.'
              }
            </DialogDescription>
          </DialogHeader>
          {!forgotSent ? (
            <div className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email" placeholder="seu@email.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="mt-1.5" data-testid="forgot-email-input"
                />
              </div>
              <Button onClick={handleForgotPassword} className="w-full bg-[#2D3E6F] hover:bg-[#1a2a4a]" disabled={!forgotEmail} data-testid="forgot-submit">
                Enviar Pedido
              </Button>
            </div>
          ) : (
            <div className="text-center space-y-3">
              <p className="text-sm text-slate-600">
                Contacte a administração em <strong>admin@adiraqui.pt</strong> ou o seu gestor direto para repor a password.
              </p>
              <Button variant="outline" onClick={() => setForgotOpen(false)}>Fechar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
